import React, { FunctionComponent, useEffect, useLayoutEffect, useReducer, useRef } from 'react';
import { Text, TextRef } from 'rs-emd-ui-atoms';
import { getLabel } from '../../../helpers/html.utils';
import {
  Action,
  QuickAddProductsByStockActionType,
  QuickAddProductsByStockProps,
  State,
} from './quick-add-products-by-stock-component.model';
import styles from './quick-add-products-by-stock-component.module.scss';
import sharedStyles from './quick-add-products-by-stock-shared.module.scss';

export const QuickAddProductsByStock: FunctionComponent<QuickAddProductsByStockProps> = (props: QuickAddProductsByStockProps) => {
  const latestQtyRef = useRef<TextRef>(null);

  const [state, dispatch] = useReducer(reducer, {
    products: [],
    lastUpdatedProducts: [],

    // add by stock
    tempProducts: [],
    newValue: '',
    newQuantity: undefined,

    //to control tab after new product is added
    tabPressed: false,
    newProductJustAdded: false,
    isNewQtyFocused: false,
  });

  // detect when tab button is pressed
  useLayoutEffect(() => {
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Tab') {
        dispatch({ type: QuickAddProductsByStockActionType.TabPress });
      }
    }
  }, [props]);

  //check when tab button is pressed after new product is added to direct focus to new value text area
  useEffect(() => {
    if (state.tabPressed && state.newProductJustAdded && state.newValue === '' && state.newQuantity === undefined) {
      latestQtyRef.current?.focus();
      dispatch({ type: QuickAddProductsByStockActionType.FocusNewProduct });
    }
  }, [state.newProductJustAdded, state.newQuantity, state.newValue, state.tabPressed]);

  //if new product stock code inputted and corresponding qty field is clicked -> update focus from new product qty to existing product qty field
  useEffect(() => {
    if (state.isNewQtyFocused && state.newProductJustAdded && state.newValue === '' && state.newQuantity === undefined) {
      latestQtyRef.current?.focus();
      dispatch({ type: QuickAddProductsByStockActionType.FocusNewProduct });
    }
  }, [state.isNewQtyFocused, state.newProductJustAdded, state.newQuantity, state.newValue]);

  //call callback when products are updated
  useEffect(() => {
    if (state.lastUpdatedProducts !== state.products) {
      dispatch({ type: QuickAddProductsByStockActionType.SetLastUpdatedProducts });
      props.onProductsUpdateCallback && props.onProductsUpdateCallback(state.products);
    }
  }, [props, state.lastUpdatedProducts, state.products]);

  //reset input values
  useEffect(() => {
    if (props.isResetting) {
      dispatch({ type: QuickAddProductsByStockActionType.Reset });
    }
  }, [props.isResetting]);

  return (
    <div className={styles['quick-add-products-by-stock-container']}>
      {/**Inputted products */}
      {state.products.map((product) => {
        return (
          <div key={product.key} className={sharedStyles['add-product-by-stock']}>
            <Text
              onBlur={() => dispatch({ type: QuickAddProductsByStockActionType.UpdateProductsWithTemp })}
              defaultValue={product.stockNo}
              disabled={props.isDisabled}
              className={sharedStyles['product-stock-no']}
              placeholder={getLabel(props.labels, props.isSearchByStockCodeOnly ? 'stock_code' : 'stock_no_or_mpn')}
              onChange={(e: any) =>
                dispatch({
                  type: QuickAddProductsByStockActionType.UpdateExistingProduct,
                  productKey: product.key,
                  stockNo: e.currentTarget.value,
                })
              }
              background='white'
            />
            <Text
              type='number'
              ref={state.products[state.products.length - 1].key === product.key ? latestQtyRef : undefined}
              onBlur={() => dispatch({ type: QuickAddProductsByStockActionType.UpdateProductsWithTemp })}
              defaultValue={product.qty ? (isNaN(product.qty) ? '' : product.qty) : ''}
              disabled={props.isDisabled}
              className={sharedStyles.qty}
              placeholder={getLabel(props.labels, 'quantity')}
              onChange={(e: any) =>
                dispatch({
                  type: QuickAddProductsByStockActionType.UpdateExistingProduct,
                  productKey: product.key,
                  qty: parseInt(e.currentTarget.value),
                })
              }
              background='white'
            />
          </div>
        );
      })}

      {/**New product inputs */}
      <div className={sharedStyles['add-product-by-stock']}>
        <Text
          value={state.newValue}
          onBlur={() => state.newValue.trim() !== '' && dispatch({ type: QuickAddProductsByStockActionType.AddNewProduct })}
          className={sharedStyles['product-stock-no']}
          placeholder={getLabel(props.labels, props.isSearchByStockCodeOnly ? 'stock_code' : 'stock_no_or_mpn')}
          background='white'
          disabled={props.isDisabled}
          onChange={(e: any) =>
            dispatch({ type: QuickAddProductsByStockActionType.UpdateNewProductInputs, inputType: 'value', input: e.target.value })
          }
        />
        <Text
          type='number'
          value={state.newQuantity || ''}
          className={sharedStyles.qty}
          placeholder={getLabel(props.labels, 'quantity')}
          background='white'
          disabled={props.isDisabled}
          onChange={(e: any) =>
            dispatch({ type: QuickAddProductsByStockActionType.UpdateNewProductInputs, inputType: 'qty', input: e.target.value })
          }
          onFocus={() => dispatch({ type: QuickAddProductsByStockActionType.NewQtyFocused, isFocused: true })}
          onBlur={() => dispatch({ type: QuickAddProductsByStockActionType.NewQtyFocused, isFocused: false })}
        />
      </div>
    </div>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case QuickAddProductsByStockActionType.SetLastUpdatedProducts:
        return {
          ...state,
          lastUpdatedProducts: state.products,
        };
      case QuickAddProductsByStockActionType.UpdateProductsWithTemp:
        return {
          ...state,
          products: JSON.stringify(state.tempProducts) === JSON.stringify(state.products) ? state.products : state.tempProducts,
        };
      case QuickAddProductsByStockActionType.UpdateExistingProduct:
        let p = [...state.products];
        let pModified = p.filter((prod) => prod.key === action.productKey)[0];
        pModified.qty = action.qty ?? pModified.qty;
        pModified.stockNo = action.stockNo ?? pModified.stockNo;

        //eliminate product with empty stock code
        if (pModified.stockNo === '') {
          p = p.filter((prod) => prod.key !== action.productKey);
        }
        //update products with modified product
        else {
          p[p.indexOf(p.filter((prod) => prod.key === action.productKey)[0])] = pModified;
        }

        return {
          ...state,
          tempProducts: p,
        };
      case QuickAddProductsByStockActionType.AddNewProduct:
        return {
          ...state,
          newProductJustAdded: true,
          products: state.products.concat({ key: state.products.length, stockNo: state.newValue, qty: state.newQuantity }),
          tempProducts: state.tempProducts.concat({ key: state.products.length, stockNo: state.newValue, qty: state.newQuantity }),
          newValue: '',
          newQuantity: undefined,
        };
      case QuickAddProductsByStockActionType.UpdateNewProductInputs:
        return {
          ...state,
          newValue: action.inputType === 'value' ? action.input : state.newValue,
          newQuantity: action.inputType === 'qty' ? parseInt(action.input) : state.newQuantity,
        };
      case QuickAddProductsByStockActionType.TabPress:
        return {
          ...state,
          tabPressed: true,
        };
      case QuickAddProductsByStockActionType.FocusNewProduct:
        return {
          ...state,
          newProductJustAdded: false,
          tabPressed: false,
        };
      case QuickAddProductsByStockActionType.NewQtyFocused:
        return {
          ...state,
          isNewQtyFocused: action.isFocused,
        };
      case QuickAddProductsByStockActionType.Reset:
        return {
          ...state,
          products: [],
          lastUpdatedProducts: [],
          tempProducts: [],

          //to control tab after new product is added
          tabPressed: false,
          newProductJustAdded: false,
          isNewQtyFocused: false,
        };
    }
  }
};
